import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { FileText, CheckCircle2, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { submitQuoteRequest, getServices } from '../api/api';

export const QuoteForm = ({ serviceParDefaut = '' }) => {
  const [services, setServices] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: serviceParDefaut,
    message: '',
    consent: false,
    website: '' // champ piège : invisible pour l'utilisateur, rempli par les robots
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await getServices();
        setServices(data);
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    };

    fetchServices();
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Le Select (Radix) n'est pas un <select> natif : la validation HTML5 ne
    // garantit pas le champ. On la refait ici pour ne pas envoyer sans service.
    if (!formData.service) {
      toast.error('Veuillez sélectionner un type de service.');
      return;
    }

    setIsSubmitting(true);

    try {
      await submitQuoteRequest(formData);
      toast.success('Demande envoyée avec succès ! Nous vous contacterons bientôt.', {
        duration: 5000
      });
      setFormData({
        name: '', email: '', phone: '', service: serviceParDefaut,
        message: '', consent: false, website: ''
      });
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : error.response?.status === 429
            ? 'Trop de demandes envoyées. Réessayez dans quelques minutes.'
            : 'Une erreur est survenue. Veuillez réessayer.';
      toast.error(message, { duration: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="quote" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card className="border-0 shadow-lg overflow-hidden">
          {/* Header with Gradient */}
          <CardHeader className="bg-blue-600 text-white p-8">
            <div className="flex items-center space-x-4">
              <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                <FileText className="w-8 h-8" />
              </div>
              <div>
                <CardTitle className="text-3xl font-bold mb-2">Demander un devis gratuit</CardTitle>
                <p className="text-blue-100">Recevez votre estimation personnalisée sans engagement</p>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-gray-700 font-semibold">
                  Nom complet *
                </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Votre nom"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  className="h-12 border-gray-200 focus:border-blue-600 focus:ring-blue-600"
                />
              </div>

              {/* Email & Phone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 font-semibold">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="votre.email@example.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                    className="h-12 border-gray-200 focus:border-blue-600 focus:ring-blue-600"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-gray-700 font-semibold">
                    Téléphone *
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+32 4 123 45 67"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    required
                    className="h-12 border-gray-200 focus:border-blue-600 focus:ring-blue-600"
                  />
                </div>
              </div>

              {/* Service Type */}
              <div className="space-y-2">
                <Label htmlFor="service" className="text-gray-700 font-semibold">
                  Type de service *
                </Label>
                <Select value={formData.service} onValueChange={(value) => handleChange('service', value)} required>
                  <SelectTrigger className="h-12 border-gray-200 focus:border-blue-600 focus:ring-blue-600">
                    <SelectValue placeholder="Sélectionnez un service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((service) => (
                      <SelectItem key={service.id} value={service.title}>
                        {service.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message" className="text-gray-700 font-semibold">
                  Message
                </Label>
                <Textarea
                  id="message"
                  placeholder="Décrivez vos besoins en détail..."
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  rows={5}
                  className="border-gray-200 focus:border-blue-600 focus:ring-blue-600 resize-none"
                />
              </div>


              {/* Champ piège : masqué à l'écran et aux lecteurs d'écran,
                  seuls les robots le remplissent. */}
              <div className="absolute left-[-9999px]" aria-hidden="true">
                <label htmlFor="website-qf">Ne pas remplir</label>
                <input
                  id="website-qf"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={formData.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                />
              </div>

              {/* Consentement RGPD */}
              <div className="flex items-start gap-3">
                <input
                  id="consent-qf"
                  type="checkbox"
                  required
                  checked={formData.consent}
                  onChange={(e) => handleChange('consent', e.target.checked)}
                  className="mt-1 w-4 h-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                />
                <Label htmlFor="consent-qf" className="text-sm text-gray-600 font-normal leading-relaxed">
                  J'accepte que mes données soient utilisées pour traiter ma demande de devis.
                  Consultez notre{' '}
                  <Link
                    to="/confidentialite"
                    target="_blank"
                    className="text-blue-600 underline hover:text-blue-700"
                  >
                    politique de confidentialité
                  </Link>
                  . *
                </Label>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-white text-lg font-semibold rounded-xl shadow-lg shadow-blue-600/30 transition-all duration-300 hover:shadow-xl hover:shadow-blue-600/40 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Envoi en cours...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Recevoir mon devis gratuit
                  </span>
                )}
              </Button>

              {/* Trust Message */}
              <p className="text-sm text-gray-500 text-center mt-4">
                Vos informations restent confidentielles et ne sont jamais transmises à des tiers.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};
